import { push } from "connected-react-router";
import { toast } from "react-toastify";
import "whatwg-fetch";

import { getTokenExpires, getUserTokenExpires, RequestError } from "utils/request";

import * as FeatureFlags from "utils/featureFlags";

import { transformUpdateDataset } from "containers/Home/reducer";
import { init as initAccounts, updateAccounts } from "containers/Accounts/reducer";
import { updateIsLeftPanelOpen } from "containers/App/reducer";
import { getSelectedAccountType } from "containers/Accounts/reducer";

import { resetAllStates, setAllStates } from "reducers";
import { autoAcceptInvites } from "containers/Admin/reducer";

import * as accountsReducer from "containers/Accounts/reducer";
import * as adminReducer from "containers/Admin/reducer";
import * as viewReducer from "containers/View/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as helpReducer from "containers/Help/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as globalReducer from "containers/App/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as chatAppReducer from "containers/ChatApp/reducer";

import * as nextReducer from "containers/Next/NextReducer";
import * as lookerSettingsReducer from "containers/LookerSettings/reducer";

import * as AuthAPI from "api/AuthAPI";
import * as TenantAPI from "api/TenantAPI";
import * as DatasetAPI from "api/DatasetAPI";
import { constructQueryParams } from "api/BaseAPI";

// import { connectExtensionHost, LookerExtensionSDK40 } from "@looker/extension-sdk";

// import { ApiSettings } from "@looker/sdk-rtl";
// import { BrowserSession, BrowserTransport, DefaultSettings } from "@looker/sdk-rtl";

// import { Looker40SDK as LookerSDK } from "@looker/sdk";

import { isEnabled } from "utils/featureFlags";
import moment from "moment";

import { getInitials } from "../../utils/getInitials";
import { VIEW_TYPE } from "containers/View/reducer";
import { tokenNeedsRefresh, userTokenPolicy } from "@/containers/Login/policies";

const TOS_DATE = "2020-07-24T12:00:00-07:00";

export const LICENSE_TYPES = {
  sample: "SAMPLE",
  evaluation: "EVALUATION",
  user: "USER"
};

const UPDATE_FIELD = "auth/UPDATE_FIELD";
const INIT_TENANTS = "auth/INIT_TENANTS";

const initialState = {
  isAuthenticated: false,
  isLoggedOn: false,
  isLoading: false,
  isTabUploading: false,
  isTableauLoading: false,
  isCreated: false,
  hasFeatureFlags: false,
  hasTableauLoaded: false,
  error: undefined,
  tenants: [],
  selection: undefined,
  selectedTenant: undefined,
  username: undefined,
  isFeatureOpen: false,
  featureName: undefined,
  loginRedirect: undefined
};

function authReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case INIT_TENANTS:
      return {
        ...state,
        ...action.data
      };
    default:
      return state;
  }
}

/* Selectors */
export function getIsWorkspaceSharing() {
  return isEnabled("workspace-sharing");
}

export function getHasAcceptedToS(state, email) {
  email = email ? email : getUserEmail(state);
  const tosDate = getUserToS(state, email);
  return Boolean(tosDate && moment.utc(tosDate).isAfter(moment(TOS_DATE)));
}

export function getIsDellDomain() {
  const domain = window.location.host;
  return Boolean(domain && domain.substr(0, 4).toLowerCase() === "dell");
}

export function getAuth(state) {
  return state?.auth;
}

export function getDomainRedirect(state) {
  return getAuth(state).domainRedirect;
}

export function getLoginRedirect(state) {
  return getAuth(state).loginRedirect;
}

export function getAuthenticated(state) {
  return getAuth(state).isAuthenticated;
}

export function getHasFeatureFlags(state) {
  return getAuth(state).hasFeatureFlags;
}

export function getHasTableauLoaded(state) {
  return getAuth(state).hasTableauLoaded;
}

export function getIsLoading(state) {
  return getAuth(state).isLoading;
}

export function getIsCreated(state) {
  return getAuth(state).isCreated;
}

export function getIsTableauLoading(state) {
  return getAuth(state).isTableauLoading;
}

export function getError(state) {
  return getAuth(state).error;
}

export function getUserNameFromState(state) {
  return getAuth(state).userName;
}

export function getTokenPromise(state) {
  return getAuth(state).tokenPromise;
}

export function getUnsortedTenants(state) {
  return getAuth(state).tenants;
}

export function getLookerAPIClient(state) {
  return window?.looker_API;
}

export function getExtensionSDK(state) {
  return window?.looker_SDK;
}

export function getTenants(state) {
  return getAuth(state).tenants
    ? getAuth(state).tenants.sort((a, b) => {
        const dateA = new Date(a.created_date);
        const dateB = new Date(b.created_date);
        return dateA < dateB ? -1 : 1;
      })
    : getAuth(state).tenants;
}

export function getTenantById(state, id) {
  const tenants = getTenants(state);
  for (const t of tenants) {
    if (t.id === id) {
      return t;
    }
  }
  return null;
}

export function getTableauData(state) {
  return getAuth(state).tableauData;
}

export function getTableauDataId(state) {
  const tableauData = getTableauData(state);

  return tableauData?.[0]?.id;
}

export function getResetMessage(state) {
  return getAuth(state).resetMessage;
}

export function getIsTabUploading(state) {
  return getAuth(state).isTabUploading;
}

export function getIsFeatureOpen(state) {
  return getAuth(state).isFeatureOpen;
}

export function getFeatureName(state) {
  return getAuth(state).featureName;
}

export function getSelectedTenant(state) {
  return getAuth(state).selectedTenant;
}

export function getSelectedTenantLicenses(state) {
  return (getSelectedTenant(state) || {}).license_types || [];
}

export function getIsSampleAccount(state) {
  return getSelectedTenantLicenses(state).includes(LICENSE_TYPES.sample);
}

export function getIsEvaluationAccount(state) {
  return getSelectedTenantLicenses(state).includes(LICENSE_TYPES.evaluation);
}

export function getSharingMode(state) {
  return getSelectedTenant(state) ? getSelectedTenant(state).share_behavior : false;
}

export function getIsSharingPrivate(state) {
  return getSharingMode(state) === "PRIVATE";
}

export function getIsLoggedOn(state) {
  return getAuth(state).isLoggedOn;
}

export function getTableauDataOptions(state) {
  const tableauData = getTableauData(state);

  return tableauData ? tableauData.map((tData, index) => ({ value: index, label: tData.name })) : undefined;
}

/* Actions */
export function initTenants(tenantState) {
  return { type: INIT_TENANTS, data: tenantState };
}

export function updateIsAuthenticated(isAuthenticated) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_FIELD,
      data: isAuthenticated,
      key: "isAuthenticated"
    });

    if (!isAuthenticated) {
      push("/login");
    }
  };
}

export function updateHasFeatureFlags(hasFeatureFlags) {
  return { type: UPDATE_FIELD, data: hasFeatureFlags, key: "hasFeatureFlags" };
}

export function updateHasTableauLoaded(hasTableauLoaded) {
  return {
    type: UPDATE_FIELD,
    data: hasTableauLoaded,
    key: "hasTableauLoaded"
  };
}

export function updateLoginRedirect(loginRedirect) {
  return { type: UPDATE_FIELD, data: loginRedirect, key: "loginRedirect" };
}

export function updateIsLoading(isLoading) {
  return { type: UPDATE_FIELD, data: isLoading, key: "isLoading" };
}

export function updateIsCreated(isCreated) {
  return { type: UPDATE_FIELD, data: isCreated, key: "isCreated" };
}

export function updateIsTabUploading(isTabUploading) {
  return { type: UPDATE_FIELD, data: isTabUploading, key: "isTabUploading" };
}

export function updateIsFeatureOpen(isFeatureOpen) {
  return { type: UPDATE_FIELD, data: isFeatureOpen, key: "isFeatureOpen" };
}

export function updateFeatureName(featureName) {
  return { type: UPDATE_FIELD, data: featureName, key: "featureName" };
}

export function updateUserName(userName) {
  return { type: UPDATE_FIELD, data: userName, key: "userName" };
}

export function updateDomainRedirect(domain) {
  return { type: UPDATE_FIELD, data: domain, key: "domainRedirect" };
}

export function updateLookerAPIClient(lookerAPIClient) {
  return { type: UPDATE_FIELD, data: lookerAPIClient, key: "lookerAPIClient" };
}

export function updateExtensionSDK(extensionSDK) {
  return { type: UPDATE_FIELD, data: extensionSDK, key: "extensionSDK" };
}

export function updateError(error) {
  return (dispatch, getState) => {
    let errorToThrow = undefined;

    if (error !== undefined) {
      if (error instanceof RequestError) {
        error = error[0]
          ? error[0].message
          : error.message && error.message.message
            ? error.message.message
            : error.message;
      } else if (typeof error === "string" || error instanceof String) {
        error = error;
      } else {
        errorToThrow = error;
        error = "An unexpected error occurred.";
      }
    }

    dispatch({
      type: UPDATE_FIELD,
      data: error ? (typeof error === "object" ? error[0].message : error) : undefined,
      key: "error"
    });

    if (errorToThrow !== undefined) {
      throw errorToThrow;
    }
  };
}

export function updateIsTokenRefresh(isTokenRefresh) {
  return { type: UPDATE_FIELD, data: isTokenRefresh, key: "isTokenRefresh" };
}

export function updateTokenPromise(tokenPromise) {
  return { type: UPDATE_FIELD, data: tokenPromise, key: "tokenPromise" };
}

function filterTenants(tenants) {
  return tenants.filter((tenant) => {
    const retIsValidTenant =
      tenant && tenant.invite && tenant.invite.status !== "DELETED" && tenant.invite.token !== null;
    return retIsValidTenant;
  });
}

export function updateTenants(tenants, useLocalTokens) {
  return (dispatch, getState) => {
    if (tenants && useLocalTokens !== true) {
      tenants.forEach((tenant) => dispatch(storeTenantToken(tenant)));
    }

    const retTenants = useLocalTokens
      ? tenants.map((t) => ({
          ...t,
          invite: { token: getTenantToken(getState(), t.id) }
        }))
      : tenants;

    return dispatch({
      type: UPDATE_FIELD,
      data: filterTenants(retTenants),
      key: "tenants"
    });
  };
}

export function updateTableauData(tableauData) {
  return { type: UPDATE_FIELD, data: tableauData, key: "tableauData" };
}

export function updateResetMessage(resetMessage) {
  return { type: UPDATE_FIELD, data: resetMessage, key: "resetMessage" };
}

export function updateSelectedTenant(selectedTenant) {
  return { type: UPDATE_FIELD, data: selectedTenant, key: "selectedTenant" };
}

export function updateIsLoggedOn(isLoggedOn) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD,
      data: isLoggedOn,
      key: "isLoggedOn"
    });
  };
}

export function updateIsTableauLoading(isTableauLoading) {
  return {
    type: UPDATE_FIELD,
    data: isTableauLoading,
    key: "isTableauLoading"
  };
}

export function setContext(context) {
  console.log("what is context? " + context);
}

export function storeItem(name, value) {
  return (dispatch, getState) => {
    const extensionSDK = getExtensionSDK(getState());
    dispatch({ type: UPDATE_FIELD, data: value, key: name });

    if (extensionSDK !== undefined) {
      if (name === "aible_auth_token" && window.looker_aKeys !== undefined) {
        window.looker_aKeys.aible_auth_token = value;
      }
      if (name === "aible_token_expires" && window.looker_aKeys !== undefined) {
        window.looker_aKeys.aible_token_expires = value;
      }
      if (value !== null && value !== undefined) {
        return extensionSDK.localStorageSetItem(name, value);
      }
    } else {
      localStorage.setItem(name, value);
    }
  };
}

export function getItem(state, name) {
  const extensionSDK = getExtensionSDK(state);

  return localStorage !== undefined && extensionSDK === undefined ? localStorage[name] : getAuth(state)?.[name];
}

export function removeItem(name) {
  return (dispatch, getState) => {
    const extensionSDK = getExtensionSDK(getState());

    if (extensionSDK) {
      extensionSDK.localStorageRemoveItem(name).then((r) => r);
    } else {
      localStorage.removeItem(name);
    }

    return dispatch({ type: UPDATE_FIELD, data: undefined, key: name });
  };
  //return window.localStorage.removeItem(name);
  //return localStorage ? localStorage.removeItem(name) : delete window[name];
}

export function storeDefaultFolder(tenantId, folderId) {
  return (dispatch, getState) => {
    return dispatch(storeItem("aible_default_folder_" + tenantId, folderId));
  };
}

export function storeTenantToken(tenant) {
  return (dispatch, getState) => {
    dispatch(storeItem("tenant_" + tenant.id, tenant.invite && tenant.invite.token));
    dispatch(storeItem("tenant_invite_new_user_" + tenant.id, tenant.new_user_invite_code));
  };
}

export function storeUserID(id) {
  return (dispatch, getState) => {
    return dispatch(storeItem("aible_user_id", id));
  };
}

export function storeUserEmail(email) {
  return (dispatch, getState) => {
    return dispatch(storeItem("aible_user_email", email));
  };
}

export function storeUserName(name) {
  return (dispatch, getState) => {
    return dispatch(storeItem("aible_user_name", name));
  };
}

export function storeUserToken(token) {
  return (dispatch, getState) => {
    return dispatch(storeItem("aible_user_token", token));
  };
}

export function storeAccountId(accountId) {
  return (dispatch, getState) => {
    return dispatch(storeItem("aible_account_id", accountId));
  };
}

export function storeLatestTenantId(tenantId) {
  return (dispatch, getState) => {
    return dispatch(storeItem("latest_tenant_id", tenantId));
  };
}

export function storeLatestAccountId(accountId) {
  return (dispatch, getState) => {
    return dispatch(storeItem("latest_acccount_id", accountId));
  };
}

export function storeChatAibleSecurityOption(optionKey, option) {
  return (dispatch, getState) => {
    return dispatch(storeItem("chat_aible_security_" + optionKey, option));
  };
}

export function storeUserToS(email) {
  return (dispatch, getState) => {
    try {
      const rawTos = getItem(getState(), "aible_tos");
      const tos = rawTos ? JSON.parse(getItem(getState(), "aible_tos")) : {};
      tos[encodeURIComponent(email)] = moment.utc();
      return dispatch(storeItem("aible_tos", JSON.stringify(tos)));
    } catch (e) {
      console.log(e);
      return email;
    }
  };
}

export function storeHasLoadedSampleDatasets(val) {
  return (dispatch, getState) => {
    return dispatch(storeItem("chataible_has_loaded_sample_datasets", val));
  };
}

export function getHasLoadedSampleDatasets(state) {
  return getItem(state, "chataible_has_loaded_sample_datasets");
}

export function getDefaultFolder(state, tenantId) {
  return getItem(state, "aible_default_folder_" + tenantId);
}

export function getTenantToken(state, id) {
  return getItem(state, "tenant_" + id);
}

export function getUserToS(state, email) {
  try {
    const rawTos = getItem(state, "aible_tos");
    if (!rawTos) {
      return false;
    }
    const tos = JSON.parse(rawTos);
    return tos && tos[encodeURIComponent(email)];
  } catch (e) {
    console.log(e);
    return false;
  }
}

export function getTenantInviteToken(id, state) {
  return getItem(state, "tenant_invite_new_user_" + id);
}

export function getUserID(state) {
  return parseInt(getItem(state, "aible_user_id"));
}

export function getUserEmail(state) {
  return getItem(state, "aible_user_email");
}

export function getUserName(state) {
  return getItem(state, "aible_user_name");
}

export function getUserInitials(state) {
  const name = getUserName(state);
  if (!name || name?.length < 2) {
    return name;
  }

  return getInitials(name);
}

export function getUserToken(state) {
  return getItem(state, "aible_user_token");
}

export function getAuthToken(state) {
  return getItem(state, "aible_auth_token");
}

export function getLatestTenantId(state) {
  return getItem(state, "latest_tenant_id");
}

export function getLatestAccountId(state) {
  return getItem(state, "latest_acccount_id");
}

export function getChatAibleSecurityOption(state, optionKey) {
  return getItem(state, "chat_aible_security_" + optionKey);
}

export function removeUserID() {
  return (dispatch, getState) => {
    return dispatch(removeItem("aible_user_id"));
  };
}

export function removeUserEmail() {
  return (dispatch, getState) => {
    return dispatch(removeItem("aible_user_email"));
  };
}

export function removeUserName() {
  return (dispatch, getState) => {
    return dispatch(removeItem("aible_user_name"));
  };
}

export function removeUserToken() {
  return (dispatch, getState) => {
    return dispatch(removeItem("aible_user_token"));
  };
}

export function removeAccountId() {
  return (dispatch, getState) => {
    return dispatch(removeItem("aible_account_id"));
  };
}

export function removeLatestTenantId() {
  return (dispatch, getState) => {
    return dispatch(removeItem("latest_tenant_id"));
  };
}

export function removeLatestAccountId() {
  return (dispatch, getState) => {
    return dispatch(removeItem("latest_acccount_id"));
  };
}

export function removeChatAibleSecurityOption(optionKey) {
  return (dispatch, getState) => {
    return dispatch(removeItem("chat_aible_security_" + optionKey));
  };
}

export function removeTenantToken(tenant) {
  return (dispatch, getState) => {
    dispatch(removeItem("tenant_" + tenant.id));
    dispatch(removeItem("tenant_invite_new_user_" + tenant.id));
  };
}

export function removeAllTenantTokens() {
  return (dispatch, getState) => {
    const toDelete = [];
    if (localStorage) {
      for (let i = 0; i < localStorage.length; i++) {
        const currKey = localStorage.key(i);
        if (currKey.substring(0, 7) === "tenant_") {
          toDelete.push(currKey);
        }
      }
    } else {
      Object.keys(window).forEach((key) => {
        if (key.substring(0, 7) === "tenant_") {
          toDelete.push(key);
        }
      });
    }
    toDelete.forEach((key) => dispatch(removeItem(key)));
  };
}

export function clearItemStorage() {
  return (dispatch, getState) => {
    dispatch(removeItem("aible_auth_token"));
    dispatch(removeItem("aible_token_expires"));

    dispatch(removeAccountId());
    // dispatch(removeLatestTenantId());
    // dispatch(removeLatestAccountId());
    dispatch(removeUserID());
    dispatch(removeUserEmail());
    dispatch(removeUserName());
    dispatch(removeUserToken());
    dispatch(removeItem("aible_user_token_expires"));
    dispatch(removeItem("aible_iap_token"));
    dispatch(removeItem("aible_iap_audience"));

    sessionStorage?.clear();

    return dispatch(removeAllTenantTokens());
  };
}

export function resetTenants() {
  return (dispatch, getState) => {
    return dispatch(updateTenants([]));
  };
}

export function pushLogin() {
  return (dispatch, getState) => {
    return dispatch(push("/login"));
  };
}

const checkRefreshOptions = {
  forceRefresh: false
};

export function checkRefresh(options = checkRefreshOptions) {
  const opts = { ...checkRefreshOptions, ...options };

  return async (dispatch, getState) => {
    const isAuthenticated = getAuthenticated(getState()) === true;

    if (isAuthenticated) {
      try {
        const userExpiration = getUserTokenExpires() ?? 0;
        const expiration = getTokenExpires() ?? 0;

        const userRefreshStatus = tokenNeedsRefresh(userExpiration);
        const tenantRefreshStatus = tokenNeedsRefresh(expiration);

        if (userRefreshStatus === "FAILED" || tenantRefreshStatus === "FAILED") {
          throw new Error(
            `token expirations failed validations unexpectedly: ${{ userRefreshStatus, tenantRefreshStatus }}`
          );
        }

        if (opts.forceRefresh) {
          console.log("Forcing refresh of tokens");
        }

        if (opts.forceRefresh || userRefreshStatus === "REFRESH") {
          await dispatch(refreshUserToken());
        }

        if (opts.forceRefresh || tenantRefreshStatus === "REFRESH") {
          // refresh authenticated tenant session token
          await dispatch(refreshToken(false, true));
        }
      } catch (e) {
        console.log("Error refreshing tokens: ", e);
        dispatch(handleLogout({ displayOnLoginScreen: `Encountered an error while refreshing tokens: ${e}` }));
      }
    }

    return Promise.resolve();
  };
}

export function refreshUserToken() {
  return (dispatch, getState) => {
    console.log("Refreshing User Token");
    return AuthAPI.refreshUserToken().then((response) => {
      dispatch(updateUserTokens(response));
      return response;
    });
  };
}

export function refreshToken(skipFetchTenants = false, isLogoutCheck = false) {
  return async (dispatch, getState) => {
    console.log("Refreshing Login");

    const response = await AuthAPI.refreshToken(false, isLogoutCheck);

    const invite = response?.invite;
    const token = invite?.token;

    if (!token) {
      console.log("Tenant token did not exist on response: ", response);
      throw Error("Unexpected response when refreshing tenant token");
    }

    const expiration_ttl = invite?.expires_ttl * 1000;
    const new_expiration = expiration_ttl + Date.now();

    if (!new_expiration) {
      console.log("Tenant token expiration was not valid: ", response);
      throw Error("Encountered NaN when refreshing tenant token");
    }

    await dispatch(storeItem("aible_token_expires", new_expiration));
    await dispatch(storeItem("aible_auth_token", token));

    const updatedTenants = getTenants(getState()).map((tenant) => {
      // Set the selected tenant to our refreshed state
      if (tenant.id === response.id) {
        return response;
      }

      return tenant;
    });

    console.log(updatedTenants);
    dispatch(updateTenants(updatedTenants));
  };
}

export function selectTenant(tenant, noLogin, uiState) {
  return (dispatch, getState) => {
    dispatch(updateSelectedTenant(tenant));

    tenant && dispatch(storeLatestTenantId(tenant.id));

    if (noLogin) {
      dispatch(storeLatestAccountId(undefined));
    }

    return dispatch(
      setLogin(
        tenant && tenant.invite && tenant.invite.token,
        tenant && tenant.invite && tenant.invite.expires_ttl,
        noLogin,
        true,
        uiState
      )
    );
  };
}

export function handlePasswordReset(username) {
  return (dispatch, getState) => {
    dispatch(updateError(undefined));
    dispatch(updateIsLoading(true));
    dispatch(updateResetMessage(undefined));

    return AuthAPI.postUser(username)
      .then((response) => {
        return AuthAPI.resetPassword(response.token)
          .then((tResponse) => {
            if (
              (tResponse && tResponse.tags && tResponse.tags === "PASSWORD_RESET_REQUIRED") ||
              tResponse.tags?.includes("PASSWORD_RESET_REQUIRED")
            ) {
              dispatch(updateResetMessage("Please check the email account below to finish resetting your password."));
            }
            dispatch(updateIsLoading(false));
          })
          .catch((error) => {
            dispatch(updateIsLoading(false));
            return dispatch(updateError(error));
          });
      })
      .catch((error) => {
        dispatch(updateIsLoading(false));
        return dispatch(updateError(error));
      });
  };
}

export function createProject() {
  const project = {
    id: "TestProjecct3",
    name: "DidICreateYou",
    creation_type: "generator",
    git_remote_url: undefined,
    connection_name: "bigquery_test",
    sql_queries_file: undefined,
    generate: "all",
    schemas: undefined
  };

  coreSDK.create_project(project).then((result) => console.log("project??", result));
}

export function createLookerModel() {
  const lookerModel = {
    name: "MyModel5",
    project_name: "DidICreateYou",
    allowed_db_connection_names: ["bigquery_test"],
    explores: [
      {
        name: "demo_data.smallbank2",
        label: "Test_6"
      }
    ]
  };

  coreSDK.create_lookml_model(lookerModel).then((result) => console.log("??", result));
}

export function registerLooker() {
  return (dispatch, getState) => {
    return connectExtensionHost().then((extensionSDK) => {
      const coreSDK = LookerExtensionSDK40.createClient(extensionSDK);

      dispatch(updateExtensionSDK(extensionSDK));
      dispatch(updateLookerAPIClient(coreSDK));

      /*
      coreSDK
        .create_user_attribute({
          name: "aible_test",
          label: "aible_test",
          type: "string",
          default_value: "TestValue",
        })
        .then((r) => console.log("create?", r));
      */
    });
  };
}

export function handleLogin(username, password, token = false, uiState) {
  return (dispatch, getState) => {
    //testStuff();

    //getRepo();

    //pushStuff();

    //return dispatch(testAuth());

    //dispatch(registerLooker());

    if (uiState) {
      try {
        uiState = JSON.parse(atob(uiState));
      } catch (e) {
        if (window.Raven !== undefined) Raven.captureException(e);
        console.log(e);
      }
    }

    dispatch(clearItemStorage());
    dispatch(updateError(undefined));
    dispatch(updateIsLoading(true));
    dispatch(updateIsCreated(false));
    const redirectUrl = getLoginRedirect(getState()) || null;
    if (!token) {
      console.log("There was no token while handling a login request");
      dispatch(handleLogout());
    }

    if (token && password) {
      return dispatch(handleSubmitPassword(password, token, uiState));
    }

    if (getLoginRedirect(getState()) !== redirectUrl) {
      dispatch(updateLoginRedirect(redirectUrl));
    }

    return AuthAPI.postUser(username)
      .then((response) => {
        if (response && response.external_authorization_url) {
          window.location.href = response.external_authorization_url;
        } else {
          return dispatch(handleSubmitPassword(password, response.token, uiState));
        }
      })
      .catch((error) => {
        dispatch(updateIsLoading(false));
        console.log(error);

        try {
          if (error && error.response) {
            error.response.then((err) => {
              const currErr = err && err[0];
              if (currErr) {
                if (currErr && currErr.key === "WRONG_APP_DOMAIN") {
                  const domain = currErr.domain;
                  if (domain) {
                    const retDomain = domain.substr(0, 4).toLowerCase() !== "http" ? "https://" + domain : domain;
                    return dispatch(updateDomainRedirect(retDomain));
                  }
                }
              }
            });
          }
        } catch (e) {
          if (window.Raven !== undefined) Raven.captureException(e);
          console.log(e);
        }

        return dispatch(updateError(error));
      });
  };
}

export function setSentryUser(email = undefined, user_id = undefined) {
  return (dispatch, getState) => {
    if (window.Raven !== undefined) {
      if (email !== undefined && email !== null && user_id !== undefined && user_id !== null) {
        // set the user context
        console.log("Setting Sentry user context");
        Raven.setUserContext({
          email: email,
          id: user_id
        });
      } else {
        // clear the user context
        console.log("Clearing Sentry user context");
        Raven.setUserContext();
      }
    }
  };
}
// Sets the user for Launch Darkly Flags and Heap tracking software
export function identifyUserForThirdPartyPackages(user, logoutOnError = true, suppressError = false) {
  return (dispatch, getState) => {
    const onDone = async (error, flags) => {
      if (logoutOnError && error != null) {
        await dispatch(globalReducer.updateIsDataMode(true));
        if (!suppressError) {
          toast.error(
            "An unexpected error occurred. Please try disabling any ad blocking software or try again later."
          );
        }
      } else {
        await dispatch(updateHasFeatureFlags(true));
        await dispatch(initFlagStates());
      }
    };

    if (user) {
      const cleanedUser = String(user).toLowerCase();
      FeatureFlags.setUserContext(cleanedUser, onDone);
    } else {
      FeatureFlags.setUserContext(undefined, onDone);
    }
  };
}

// Set new reducer defaults based on user flags
export function initFlagStates() {
  return (dispatch, getState) => {
    let defaultView = localStorage.getItem("default_view");
    if (!defaultView) {
      // empty localStorage returns null
      if (localStorage.getItem("dataset_default_page") != undefined) {
        if (localStorage.getItem("dataset_default_page") === "true") {
          localStorage.setItem("default_view", "datasets");
        }
        localStorage.removeItem("dataset_default_page");
      }
    }

    let defaultDatasetView = localStorage.getItem("dataset_default_view");

    if (!defaultDatasetView) {
      localStorage.setItem("dataset_default_view", "TILE");
    }

    dispatch(chatGPTReducer.resetGPT());
  };
}

export function createUserInfoQuery(state) {
  const queryArr = [];

  const email = getUserEmail(state) || "";
  if (email) {
    queryArr.push("email=" + encodeURIComponent(email));
  }
  const name = (getUserName(state) || "").split(" ");
  if (name) {
    let firstName, lastName;
    if (name.length > 1) {
      lastName = name.pop();
      queryArr.push("lastname=" + encodeURIComponent(lastName));
    }
    firstName = name.join(" ");
    queryArr.push("firstname=" + encodeURIComponent(firstName));
  }

  return constructQueryParams(queryArr);
}

export function updateUserTokens(response) {
  return (dispatch, getState) => {
    const userToken = response?.user_token;
    const userTokenExpires = response.user_token_expires_at * 1000;

    dispatch(storeItem("aible_user_token_expires", userTokenExpires));
    dispatch(storeItem("aible_user_token", userToken));

    if (response.iap) {
      const iapToken = response.iap.token;
      dispatch(storeItem("aible_iap_token", iapToken));

      dispatch(storeItem("aible_iap_audience", response.iap.audience));
    }
  };
}

export function handleSubmitPassword(password, token, uiState) {
  return (dispatch, getState) => {
    return AuthAPI.postPassword(token, password)
      .then((tResponse) => {
        dispatch(updateIsLoading(false));
        dispatch(storeUserID(tResponse.id));
        dispatch(storeUserName(tResponse.fullname));
        dispatch(storeUserEmail(tResponse.email));
        dispatch(storeUserToS(tResponse.email));
        dispatch(setSentryUser(tResponse.email, tResponse.id));

        // dispatch(updateUserName(tResponse.fullname));
        try {
          dispatch(identifyUserForThirdPartyPackages(tResponse.email));
        } catch (e) {
          if (window.Raven !== undefined) Raven.captureException(e);
          console.log(e);
        }
        if (tResponse.status === AuthAPI.NOT_ACTIVATED) {
          dispatch(updateError(`This account hasn't been activated yet.`));
        } else {
          dispatch(updateUserTokens(tResponse));

          let hideToasts = undefined;
          if (uiState?.type === "SHARE") {
            hideToasts = true;
          }

          // wait to accept invites, if any
          dispatch(autoAcceptInvites(tResponse.tenants, hideToasts)).then((acceptedTenants) => {
            const retAcceptedTenants = acceptedTenants.map((tenant) => ({
              value: tenant.id,
              label: tenant.fullname,
              extra: tenant,
              ...tenant
            }));

            dispatch(updateTenants(retAcceptedTenants));

            if (uiState?.type === "SHARE") {
              toast("Congratulations! Your Aible account setup is complete", {
                type: toast.TYPE.SUCCESS,
                position: toast.POSITION.TOP_CENTER,
                autoClose: 3500
              });
            }

            const latestTenantId = getLatestTenantId(getState());
            const redirectUrl = getLoginRedirect(getState()) || null;
            const tenants = getTenants(getState());

            if (tenants && tenants.length === 1) {
              return dispatch(selectTenant(tenants[0], false, uiState));
            } else if (tenants && tenants.length === 0) {
              // dispatch(
              //   handleRegisterTenant(
              //     tResponse.fullname + " - Tenant",
              //     tResponse.user_token,
              //     true
              //   )
              // );
              toast("No Workspaces found. Please contact Aible Support.");
              return dispatch(updateIsLoggedOn(true));
            } else if (latestTenantId !== undefined) {
              const selTenant = tenants.filter((t) => t.id === Number(latestTenantId));

              return selTenant && selTenant.length === 1
                ? dispatch(selectTenant(selTenant[0], undefined, uiState))
                : dispatch(updateIsLoggedOn(true));
            } else if (tResponse.message) {
              dispatch(updateError(tResponse.message));
            } else {
              return dispatch(updateIsLoggedOn(true));
            }
          });
        }
      })
      .catch((error) => {
        dispatch(updateIsLoading(false));
        return dispatch(updateError(error));
      });
  };
}

export function setLogin(token, ttl, noLogin, overrideAccounts = false, uiState) {
  return (dispatch, getState) => {
    dispatch(storeItem("aible_auth_token", token));

    if (ttl) {
      dispatch(storeItem("aible_token_expires", new Date().getTime() + ttl * 1000));
    } else {
      console.log("Invalid ttl. Defaulting to a couple of hours.");
      const newTTL = Date.now() + 8 * 1000 * 60 * 60; // 8 hours from now
      dispatch(storeItem("aible_token_expires", newTTL));
    }

    const redirectUrl = getLoginRedirect(getState()) || null;

    // TODO: This causes a redirect
    dispatch(handleStateReset({ resetFolders: true, resetAccounts: true }));

    if (redirectUrl) {
      return dispatch(helpReducer.goToHelpUri(redirectUrl, true));
    }

    /*
    const extensionSDK = getExtensionSDK(getState());
    if (extensionSDK) {
      console.log("are we setting sotrage?");

      extensionSDK
        .localStorageSetItem("auth_reducer", "Wqewqeqweq")
        .then((r) => console.log("authtoken sseeet?", r));
    }
    */

    //dispatch(testAuth());
    let checkShareApp = () => Promise.resolve();
    if (uiState?.type === "SHARE") {
      checkShareApp = () => {
        const url = uiState?.url;

        const userId = getUserID(getState());
        const email = getUserEmail(getState());

        const payload = {
          role_user: userId,
          role_user_email: email,
          role_type: chatAppReducer.CHATAPP_ROLE
        };

        return dispatch(chatAppReducer.executePresignedShare(url, payload, uiState?.name));
      };
    }

    return checkShareApp().then(() => dispatch(initAccounts(noLogin, overrideAccounts, uiState)));
  };
}

export function testAuth() {
  return (dispatch, getState) => {
    const browserTransport = new BrowserTransport();

    console.log("wewqe", browserTransport);

    const iSettings = new ApiSettings({
      base_url: "https://aible.cloud.looker.com",
      client_id: "KfsjnbW3qbSNqS2f3JXJ",
      client_secret: "nZkm2kRyd4vD98XVjgVm3ryX"
    });

    iSettings.client_id = "KfsjnbW3qbSNqS2f3JXJ";
    iSettings.client_secret = "nZkm2kRyd4vD98XVjgVm3ryX";
    iSettings.redirect_uri = "http://localhost:3000/login";
    iSettings.looker_url = "https://aible.cloud.looker.com";

    console.log("aapa", iSettings);

    const browserSession = new BrowserSession(iSettings);

    //browserSession.authenticate().then(wat => console.log("qweqweq",wat));

    console.log("wat are settings", iSettings, browserSession);

    const lookerSDK = new LookerSDK(browserSession);

    lookerSDK.register_oauth_client_app("84", {
      redirect_uri: "http://localhost:3000/login",
      name: "TestApp"
    });

    dispatch(updateLookerAPIClient(lookerSDK));
  };
}

// TODO: There is a reducer key that needs to be saved in order to no redirect that is not being saved
export function handleStateReset({ resetFolders = false, resetAccounts = false } = {}) {
  return (dispatch, getState) => {
    const tenants = getAuth(getState());

    const admin = adminReducer.getAdmin(getState());
    const view = viewReducer.getGlobal(getState());
    const global = globalReducer.getGlobal(getState());

    const cleanState = {
      admin: admin,
      auth: tenants,
      view: view,
      global: global
    };
    if (!resetAccounts) {
      const accounts = accountsReducer.getBaseAccounts(getState());
      cleanState.accounts = accounts;
    }
    if (!resetFolders) {
      const folders = foldersReducer.getGlobal(getState());
      cleanState.folders = folders;
    }
    dispatch(setAllStates(cleanState));
    const userEmail = getUserEmail(getState());
    const userId = getUserID(getState());
    dispatch(setSentryUser(userEmail, userId));
    dispatch(identifyUserForThirdPartyPackages(userEmail, true, true));
    return;
  };
}

const handleLogoutOptions = {
  redirect: true,
  displayOnLoginScreen: undefined
};

export function handleLogout(options = handleLogoutOptions) {
  const { redirect, displayOnLoginScreen } = { ...handleLogoutOptions, ...options };
  return (dispatch, getState) => {
    // Clear Local Storage
    dispatch(clearItemStorage());

    dispatch(updateAccounts(undefined));
    dispatch(updateIsLeftPanelOpen(false));
    dispatch(updateIsAuthenticated(false));
    dispatch(resetTenants());

    dispatch(identifyUserForThirdPartyPackages(undefined, false));
    dispatch(setSentryUser());

    const isGoogle = globalReducer.getIsGoogleMode(getState());
    const googleSheetsData = globalReducer.getGoogleSheetsData(getState());

    dispatch(resetAllStates());

    if (isGoogle) {
      dispatch(globalReducer.updateIsGoogleMode(isGoogle));
      dispatch(globalReducer.updateGoogleSheetsData(googleSheetsData));
    }

    const location = getState().router.location;

    const from = location?.state?.from;

    if (redirect && from === undefined && location.pathname !== "/login") {
      console.log("Redirecting to login screen.");
      return dispatch(push("/login", { from: location, displayOnLoginScreen }));
    }
  };
}

export function handleRegisterTenant(fullname, accessKey, shouldSelect = false) {
  return (dispatch, getState) => {
    const retAccessKey = accessKey ? accessKey : getUserToken(getState());

    return AuthAPI.registerTenant(fullname, retAccessKey)
      .then((response) => {
        if (accessKey) {
          if (shouldSelect === true) {
            dispatch(selectTenant(response && response.id));
          } else {
            dispatch(
              setLogin(
                response && response.invite && response.invite.token,
                response && response.invite && response.invite.expires_ttl
              )
            );
          }
        } else {
          dispatch(fetchTenants());
        }
      })
      .catch((error) => {
        dispatch(updateIsLoading(false));
        return dispatch(updateError(error));
      });
  };
}

export function handleAzureRegister(fullname, username, company, token) {
  return (dispatch, getState) => {
    dispatch(updateError(undefined));
    dispatch(updateIsLoading(true));
    return AuthAPI.activateAzureUser(fullname, username, company, token)
      .then((response) => {
        dispatch(updateIsLoading(false));
        dispatch(updateIsCreated(true));
        dispatch(push("/login/azure"));
      })
      .catch((error) => {
        dispatch(updateIsLoading(false));
        dispatch(updateIsCreated(false));
        return dispatch(updateError(error));
      });
  };
}

export function handleRegister(fullname, username, password, inviteToken, uiState) {
  return (dispatch, getState) => {
    dispatch(updateError(undefined));
    dispatch(updateIsLoading(true));

    return AuthAPI.registerUser(fullname, username, password, inviteToken, uiState)
      .then((response) => {
        dispatch(updateIsLoading(false));
        if (response?.status === "ACTIVE") {
          return dispatch(handleLogin(username, password));
        }
        dispatch(updateIsCreated(true));
        dispatch(pushLogin());
      })
      .catch((error) => {
        dispatch(updateIsLoading(false));
        dispatch(updateIsCreated(false));
        return dispatch(updateError(error));
      });
  };
}

function serializeRegisterBulkValue(value) {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return value === undefined || value === null ? "" : value.toString();
}

function serializeRegisterBulkCSV(users) {
  const headers = ["email", "fullname", "password"];
  const escapeCSVValue = (value) => {
    const serializedValue = serializeRegisterBulkValue(value);
    if (serializedValue === "") {
      return "";
    }
    return `"${serializedValue.replace(/"/g, '""')}"`;
  };

  const rows = users.map((user) => {
    if (!user?.email || !user?.fullname || !user?.password) {
      throw new Error("Every bulk registration user must have fullname, email, and password.");
    }

    return [user.email, user.fullname, user.password].map(escapeCSVValue).join(",");
  });

  return [headers.map(escapeCSVValue).join(","), ...rows].join("\n");
}

export function handleRegisterBulk(users, accessKey) {
  return (dispatch, getState) => {
    const retAccessKey = accessKey ? accessKey : getAuthToken(getState());
    const csvBody = serializeRegisterBulkCSV(users);

    return AuthAPI.registerUsersBulk(csvBody, retAccessKey);
  };
}

export function handleInvite(tenant) {
  return (dispatch, getState) => {
    if (tenant && tenant.id) {
      /* Old and slow
      const tenantToken = getTenantToken(tenant.id);

      if (tenantToken) {
        AuthAPI.refreshToken(tenantToken).then(response => {
          const inviteToken = response.new_user_invite_code;
          return window.open("/registration/" + inviteToken);
        });
      }

      */

      const encodedToken = encodeURIComponent(btoa(getTenantInviteToken(getState(), tenant.id)));
      return window.open("/registration/" + encodedToken);
    }
  };
}

export function handleTableauUpload(index) {
  return (dispatch, getState) => {
    const datasource = getTableauData(getState())[index ? index : 0];

    const calcFields = datasource.fields.filter((field) => field.isCalculatedField === true).map((field) => field.name);

    calcFields.push("Table Name");

    dispatch(updateIsTabUploading(true));

    datasource.getUnderlyingDataAsync().then((data) => {
      const rows = [];

      let ignoreIndices = [];
      let count = 0;
      for (let col of data.columns) {
        if (calcFields.indexOf(col.fieldName) !== -1) {
          ignoreIndices[count] = true;
        }

        count++;
      }

      rows.push(
        data.columns
          .filter((col, i) => ignoreIndices[i] !== true)
          .map((col, index) => col.fieldName)
          .join(",") + "\n"
      );

      let index = 0;
      for (const row of data.data) {
        rows.push(
          row
            .filter((val, i) => ignoreIndices[i] !== true)
            .map((val) => (val && val.value ? '"' + val.value.toString().replace("%null%", "") + '"' : ""))
            .join(",") + (index + 1 < data.data.length ? "\n" : "")
        );

        index++;
      }

      const file = new File(rows, datasource.name + ".csv", {
        type: "text/csv"
      });

      const accountType = getSelectedAccountType(getState());

      const folderId = foldersReducer.getSelectedFolder(getState());
      const accountId = accountsReducer.getSelectedAccountId(getState());

      return DatasetAPI.uploadDataset(file, {
        type: accountType,
        folderId: folderId,
        accountId: accountId
      }).then((response) => {
        dispatch(updateIsTabUploading(false));
        return dispatch(transformUpdateDataset(response.id, response));
      });
    });
  };
}

export function validateUserTokenStatus(userToken) {
  return async (dispatch, getState) => {
    let userToken = getUserToken(getState());

    if (userTokenPolicy(userToken).result === "DENY") {
      // last chance
      await dispatch(refreshUserToken());
      userToken = getUserToken(getState());

      if (userTokenPolicy(userToken).result === "DENY") {
        console.log("Failed to refresh userToken");
        dispatch(handleLogout());
      }
    }

    return userToken;
  };
}

export function fetchTenants(skipInitAccounts = false) {
  return async (dispatch, getState) => {
    const userToken = await dispatch(validateUserTokenStatus());
    const tenants = await TenantAPI.getTenants(userToken);

    dispatch(updateTenants(tenants));

    if (!skipInitAccounts) {
      return dispatch(initAccounts(true));
    } else {
      return Promise.resolve();
    }
  };
}

export function updateTenant(id, data) {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(updateIsLoading(true));
    const currTenants = getUnsortedTenants(state) || [];
    dispatch(updateTenants([], true));
    const tenant = getTenantById(state, id);
    const accessKey = (tenant && tenant.invite && tenant.invite.token) || getTenantToken(getState(), id);
    return TenantAPI.updateTenant(accessKey, id, data).then((tenant) => {
      const newTenants = currTenants.map((t) => (t.id === tenant.id ? tenant : t));
      dispatch(updateTenants(newTenants, true));
      const currSelected = getSelectedTenant(state);
      dispatch(updateIsLoading(false));
      if (currSelected.id === tenant.id) {
        const nTenants = getTenants(getState());
        for (const t of nTenants) {
          if (t.id === tenant.id) {
            dispatch(updateSelectedTenant(t));
          }
          break;
        }
        const currAccount = accountsReducer.getSelectedAccountId(state);
        return dispatch(accountsReducer.handleSelect(currAccount));
      }
    });
  };
}

export function handleChangeSharing(tenantId, mode) {
  return (dispatch, getState) => {
    switch (mode) {
      case "PRIVATE":
      case "PUBLIC":
        const update = { share_behavior: mode };
        return dispatch(updateTenant(tenantId, update));
      default:
        return Promise.resolve();
    }
  };
}

export function init() {
  return (dispatch, getState) => {
    dispatch(initTableau());

    const userToken = getUserToken(getState());

    const userEmail = getUserEmail(getState());
    const userId = getUserID(getState());
    if (userEmail !== null && userEmail !== undefined) {
      dispatch(identifyUserForThirdPartyPackages(userEmail));
    }
    dispatch(setSentryUser(userEmail, userId));

    return dispatch(checkRefresh()).then(() =>
      TenantAPI.getTenants(userToken).then((tenants) => {
        dispatch(updateTenants(tenants, true));

        const authToken = getAuthToken(getState());

        const nTenants = getTenants(getState());

        nTenants.forEach((t) => t && t.invite && t.invite.token === authToken && dispatch(updateSelectedTenant(t)));
        dispatch(initAccounts(true));
      })
    );
  };
}

export function initTableau() {
  return (dispatch, getState) => {
    dispatch(updateIsCreated(false));
    /*
    let tableauWDC = document.createElement('script');
    tableauWDC.setAttribute('src','https://connectors.tableau.com/libs/tableauwdc-2.3.latest.js');
    document.head.appendChild(tableauWDC);

    // Create the connector object
    var myConnector = tableau.makeConnector();

    console.log(window._tableau);

    var DOMContentLoaded_event = window.document.createEvent("Event")
                DOMContentLoaded_event.initEvent("DOMContentLoaded", true, true)
                window.document.dispatchEvent(DOMContentLoaded_event)

    //window._tableau.triggerInitialization();

    // Define the schema
    myConnector.getSchema = function(schemaCallback) {
        var cols = [{
            id: "id",
            dataType: tableau.dataTypeEnum.string
        }, {
            id: "mag",
            alias: "magnitude",
            dataType: tableau.dataTypeEnum.float
        }, {
            id: "title",
            alias: "title",
            dataType: tableau.dataTypeEnum.string
        }, {
            id: "location",
            dataType: tableau.dataTypeEnum.geometry
        }];

        var tableSchema = {
            id: "earthquakeFeed",
            alias: "Earthquakes with magnitude greater than 4.5 in the last seven days",
            columns: cols
        };

        schemaCallback([tableSchema]);
    };

    // Download the data
    myConnector.getData = function(table, doneCallback) {
        $.getJSON("https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_week.geojson", function(resp) {
            var feat = resp.features,
                tableData = [];

            // Iterate over the JSON object
            for (var i = 0, len = feat.length; i < len; i++) {
                tableData.push({
                    "id": feat[i].id,
                    "mag": feat[i].properties.mag,
                    "title": feat[i].properties.title,
                    "location": feat[i].geometry
                });
            }

            table.appendRows(tableData);
            doneCallback();
        });
    };

    tableau.registerConnector(myConnector);

    */

    if (window.tableau !== undefined) {
      window.tableau.extensions
        .initializeAsync()
        .then(() => {
          dispatch(updateIsTableauLoading(true));

          const worksheet = window.tableau.extensions.dashboardContent.dashboard.worksheets[0];

          console.log("Tableau Loaded. Worksheet: ", worksheet);

          dispatch(globalReducer.updateIsTableauMode(true));

          worksheet.getDataSourcesAsync().then((datasource) => {
            console.log("Tableau Datasoure: ", datasource);

            const tableauDataId = datasource?.[0]?.id;

            dispatch(updateTableauData(datasource));

            dispatch(checkForTableauExplore());
          });
        })
        .catch((e) => {});
    }

    return Promise.resolve();
  };
}

export function checkForTableauExplore() {
  return (dispatch, getState) => {
    const tableauDataId = getTableauDataId(getState());
    const nexts = viewReducer.getReports(getState());

    const foundNext = nexts.find(
      (next) => tableauDataId !== undefined && next?.metadata?.explorations?.[0]?.tableauDataId === tableauDataId
    );

    if (foundNext !== undefined) {
      dispatch(push("/project_next/" + foundNext?.id));
    }

    if (nexts === undefined || nexts.length === 0) {
      return setTimeout(() => dispatch(checkForTableauExplore()), 500);
    } else {
      dispatch(updateIsTableauLoading(false));
    }

    console.log("what are all nexts, tableauDataId, foundNext", nexts, tableauDataId, foundNext);
  };
}

function loadJSON(path, cb, isLocal) {
  var obj = new XMLHttpRequest();
  obj.overrideMimeType("application/json");
  if (isLocal) {
    obj.open("GET", "../json/" + path + ".json", true);
  } else {
    obj.open("GET", "http://jsonplaceholder.typicode.com/" + path, true);
  }
  obj.onreadystatechange = function () {
    if (obj.readyState == 4 && obj.status == "200") {
      cb(obj.responseText);
    }
  };
  obj.send(null);
}

export default authReducer;
